import React, { useState, useRef, useCallback } from 'react';
import useInvisibleRecaptcha from '../hooks/useInvisibleRecaptcha';
import { apiFetch } from '../services/api';
import { RECAPTCHA_SITE_KEY } from '../config/env';
// The form's visual styles (cp-form / cp-field / cp-success / cp-submit)
// live in ContactPage.css. Import it here so the component is
// self-contained wherever it's dropped rather than silently depending on
// a page it happens to be rendered next to.
import '../pages/ContactPage.css';

// Reusable contact form. Owns its own submit / success / reCAPTCHA
// state so any caller can drop it in without re-implementing the flow.
// POSTs name/email/phone/message to /api/contacts behind invisible
// reCAPTCHA — the single path for contact enquiries.
export default function ContactForm({ onSuccess, submitLabel = 'Send Message' }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formRef = useRef(null);

  const submitForm = useCallback(async (token) => {
    const form = formRef.current;
    if (!form) return;
    const fd = new FormData(form);
    const data = {};
    fd.forEach((v, k) => { data[k] = v; });
    if (token) data.recaptchaToken = token;

    try {
      const res = await apiFetch('/api/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
        if (onSuccess) onSuccess();
      } else {
        const err = await res.json().catch(() => ({}));
        setSubmitError(err.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    }
    recaptchaReset();
    setSubmitting(false);
  // recaptchaReset is stable from the hook; including it would rebuild
  // submitForm every render and break the recaptcha callback reference.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // See useInvisibleRecaptcha: a dismissed, expired or blocked challenge never
  // calls onVerify, which would leave this form stuck on "Sending...".
  const handleRecaptchaUnavailable = useCallback((reason) => {
    setSubmitting(false);
    setSubmitError(
      reason === 'blocked'
        ? "We couldn't load the spam check - an ad blocker or privacy extension is usually the cause. Please allow it for this page and try again, or email info@asgshading.com.au or call +61 422 439 499."
        : reason === 'expired'
          ? "The spam check timed out. Please try again."
          : "The spam check could not be completed. Please try again, or email info@asgshading.com.au or call +61 422 439 499."
    );
  }, []);

  const { containerRef: recaptchaRef, execute: recaptchaExecute, reset: recaptchaReset, prepare: loadRecaptcha } =
    useInvisibleRecaptcha({ siteKey: RECAPTCHA_SITE_KEY, onVerify: submitForm, onUnavailable: handleRecaptchaUnavailable });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);
    recaptchaExecute();
  };

  if (submitted) {
    return (
      <div className="cp-success">
        <svg width="48" height="48" fill="none" style={{ stroke: 'var(--asg-accent)' }} strokeWidth="1.5" viewBox="0 0 24 24">
          <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        <h2>Message Sent</h2>
        <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
        <button className="cp-reset-btn" onClick={() => setSubmitted(false)}>Send Another Message</button>
      </div>
    );
  }

  return (
    <form className="cp-form" ref={formRef} onSubmit={handleSubmit} onFocus={loadRecaptcha}>
      <div className="cp-form-row">
        <div className="cp-field">
          <label>Full Name *</label>
          <input type="text" name="full_name" required placeholder="Your name" autoComplete="name" />
        </div>
        <div className="cp-field">
          <label>Email *</label>
          <input type="email" name="email" required placeholder="your@email.com" autoComplete="email" />
        </div>
      </div>
      <div className="cp-field">
        <label>Phone</label>
        <input type="tel" name="phone" placeholder="04XX XXX XXX" autoComplete="tel" />
      </div>
      <div className="cp-field">
        <label>Message *</label>
        <textarea name="message" required rows="5" placeholder="Tell us about your project, questions, or how we can help..."></textarea>
      </div>
      {RECAPTCHA_SITE_KEY && <div ref={recaptchaRef}></div>}
      {submitError && <p className="asg-form-error" role="alert">{submitError}</p>}
      <button type="submit" className="cp-submit" disabled={submitting}>
        {submitting ? 'Sending...' : submitLabel}
      </button>
    </form>
  );
}
